import { Button, Card } from '@design-system';
import { PREFILL_CURRENCY_QUESTION_IDS, PREFILL_QUESTION_LABELS, PREFILL_SECTIONS } from '@shared';

import { WaterlilyLogo } from '@client/components/WaterlilyLogo';

import type { PrefillReviewResponse } from '@client/api/waterlily';

interface PrefillReviewScreenProps {
  review: PrefillReviewResponse;
  isSubmitting: boolean;
  onContinue: () => void;
  /** Hidden for returning clients, whose answers may have changed in the form since. */
  showAnswers?: boolean;
}

const PREFILL_REVIEW_LABELS: Record<string, string> = {
  clientBirthDate: 'Birth date',
  clientGenderAtBirth: 'Sex at birth',
  clientZipCode: 'Zip code',
  clientMaritalStatus: 'Marital status',
  partnerFirstName: "Partner's first name",
  partnerLastName: "Partner's last name",
  householdHouseholdSize: 'People in your household',
  householdTotalAssetsValue: 'Net worth',
  financialAssetTypes: 'Asset types',
  clientProfessionalSupportNetworkList: 'Professionals you work with',
};

function formatValue(questionId: string, value: string | string[]): string {
  if (Array.isArray(value)) return value.join(', ');

  if (questionId === 'clientBirthDate' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-');
    return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  if (PREFILL_CURRENCY_QUESTION_IDS.includes(questionId)) {
    const amount = Number(value);
    if (Number.isFinite(amount)) return `$${amount.toLocaleString('en-US')}`;
  }

  return value;
}

function initialsOf(firstName: string | null, lastName: string | null): string {
  return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();
}

export function PrefillReviewScreen({
  review,
  isSubmitting,
  onContinue,
  showAnswers = true,
}: PrefillReviewScreenProps) {
  const { advisorFirstName, advisorLastName, advisorFirmName, prefilledFields } = review;
  const advisorName = [advisorFirstName, advisorLastName].filter(Boolean).join(' ');
  const attribution = advisorFirmName ? `${advisorName}, ${advisorFirmName}` : advisorName;

  const answers = PREFILL_SECTIONS.flatMap(({ questionIds }) =>
    questionIds
      .filter(questionId => questionId in prefilledFields)
      .map(questionId => {
        const rawValue = prefilledFields[questionId];
        return {
          questionId,
          label: PREFILL_REVIEW_LABELS[questionId] ?? PREFILL_QUESTION_LABELS[questionId],
          value: formatValue(questionId, rawValue),
          selectedOptions: Array.isArray(rawValue) ? rawValue : null,
        };
      }),
  );

  const answerCount = answers.length;

  return (
    <div className="bg-muted/30 min-h-screen">
      <header className="flex items-center border-b bg-white px-6 py-4">
        <WaterlilyLogo className="h-7 w-auto" />
      </header>

      <div className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-10">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <span className="bg-primary text-primary-foreground flex size-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
              {initialsOf(advisorFirstName, advisorLastName)}
            </span>
            <div className="flex flex-col">
              <span className="text-sm text-gray-600">Your advisor</span>
              <span className="font-semibold text-gray-950">{attribution}</span>
            </div>
          </div>

          <h1 className="text-3xl font-bold text-gray-950 sm:text-4xl">
            {showAnswers ? 'Review what your advisor shared' : 'Welcome back to your assessment'}
          </h1>

          <p className="max-w-2xl text-lg text-gray-700">
            {showAnswers
              ? `${advisorName} pre-filled parts of your assessment using details they had on file. You can review the pre-filled fields here, and if anything looks off you can correct it later on when you reach that question in the assessment.`
              : `${advisorName} filled in some of your answers. You will see those questions already answered as you work through your assessment, and you can change any of them.`}
          </p>
        </div>

        {showAnswers && (
          <Card className="p-0">
            <div className="px-6 py-3.5 text-sm font-medium text-gray-950">
              {answerCount} answer{answerCount === 1 ? '' : 's'}{' '}
              {advisorFirstName ? `from ${advisorFirstName}` : 'ready'}
            </div>

            <dl>
              {answers.map(({ questionId, label, value, selectedOptions }) => (
                <div
                  key={questionId}
                  className={
                    selectedOptions
                      ? 'flex min-h-11 items-start gap-4 border-t border-gray-200 px-6 py-3'
                      : 'flex min-h-11 items-center gap-4 border-t border-gray-200 px-6 py-3'
                  }
                >
                  <dt
                    id={`prefill-review-label-${questionId}`}
                    className={`flex-1 text-sm text-gray-600 ${selectedOptions ? 'pt-0.5' : ''}`}
                  >
                    {label}
                  </dt>
                  <dd className="ml-auto w-[65%] max-w-[65%] shrink-0 break-words text-left text-base font-medium text-gray-950 sm:w-[44%] sm:max-w-[44%]">
                    {selectedOptions ? (
                      <ul
                        aria-labelledby={`prefill-review-label-${questionId}`}
                        className="flex flex-col items-start gap-2"
                      >
                        {selectedOptions.map(option => (
                          <li key={option}>{option}</li>
                        ))}
                      </ul>
                    ) : (
                      value
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          </Card>
        )}

        <Button
          className="w-fit"
          size="lg"
          disabled={isSubmitting}
          onClick={onContinue}
        >
          {isSubmitting ? 'Continuing…' : 'Continue to my assessment'}
        </Button>
      </div>
    </div>
  );
}
